import React from "react";
import { Grid, Typography, Stack, Paper } from "@mui/material";

import { User } from "../../../../types";
import RoleChip, {
  type RoleChipValue,
} from "../../../../components/chips/role-chip";
import TextCopyChip from "../../../../components/chips/text-copy-chip";
import { UserAvatar } from "../../../../components/avatars";
import EditableRoleChip from "../../../../components/chips/editable-role-chip";
import { TeamSummary } from "../../../../types.new";
import { getTeamRoleInfo } from "../../../../utils/get-team-role-info";

interface UserCardProps {
  user: User;
  team: TeamSummary;
  canEdit: boolean;
}

const UserCard: React.FC<UserCardProps> = ({ user, team, canEdit }) => {
  const role: RoleChipValue = getTeamRoleInfo(user, team.teamId);
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Paper
        sx={{
          p: 2,
          borderRadius: "0.5rem",
        }}
      >
        <Stack spacing={2} sx={{ flex: 1 }}>
          {/* Header with avatar, name and role */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="flex-start"
            sx={{ mb: 2 }}
          >
            {/* Avatar and user info */}
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ flex: 1 }}
            >
              <UserAvatar text={user.fullName || user.email || "Guest"} />
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{
                    fontSize: "0.95rem",
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {user.fullName}
                </Typography>
                {user.jobTitle && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontSize: "0.8rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {user.jobTitle}
                  </Typography>
                )}
              </Stack>
            </Stack>
            {canEdit && role !== "Client Admin" ? (
              <EditableRoleChip
                user={user}
                team={team}
                chipSize="small"
                chipHeight="24px"
                chipFontSize="0.7rem"
              />
            ) : (
              <RoleChip
                role={role}
                size="small"
                sx={{
                  height: 24,
                  fontSize: "0.7rem",
                }}
              />
            )}
          </Stack>

          {/* Email section */}
          {user.email ? (
            <TextCopyChip text={user.email} />
          ) : (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ fontSize: "0.8rem" }}
            >
              No email
            </Typography>
          )}
        </Stack>
      </Paper>
    </Grid>
  );
};

export default UserCard;
